/**
 * Public bundle detail (TNK-212 phase 2a) — `/shop/bundle/$id`. Shows the
 * locked bundle (cover, price, description) and a READ-ONLY preview of its
 * member cards. The members are deliberately NOT individually addable or
 * linked: they're held In Package as part of the locked bundle, so a per-card
 * purchase path would be misleading (and their own /shop/card/$id is gone from
 * the singles feed). The bundle-level reserve/add-to-cart CTA lands in 2b.
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Package,
  ShoppingCart,
  X,
} from "lucide-react";

import { imageUrl } from "@/features/imageUpload";
import {
  fetchStorefrontBundle,
  type StorefrontBundleDetail,
  type StorefrontCard,
} from "@/features/storefront";
import { useShopCart } from "./shop";

export const Route = createFileRoute("/shop/bundle/$id")({
  component: ShopBundleDetail,
});

type LoadState =
  | { phase: "loading" }
  | { phase: "ready"; detail: StorefrontBundleDetail }
  | { phase: "notfound" }
  | { phase: "error" };

function ShopBundleDetail() {
  const { id } = Route.useParams();
  const [state, setState] = useState<LoadState>({ phase: "loading" });
  // Bundle descriptions are eBay-style listing copy — long. Clamp by default
  // so it doesn't dominate the page; "Show more" expands it.
  const [descExpanded, setDescExpanded] = useState(false);
  // Quick-view lightbox over the member-card gallery (null = closed).
  const [quickIndex, setQuickIndex] = useState<number | null>(null);
  const cart = useShopCart();

  useEffect(() => {
    let cancelled = false;
    setState({ phase: "loading" });
    void fetchStorefrontBundle(id)
      .then((d) => {
        if (cancelled) return;
        setState(d ? { phase: "ready", detail: d } : { phase: "notfound" });
      })
      .catch(() => {
        if (!cancelled) setState({ phase: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (state.phase === "loading") {
    return <p className="py-12 text-center text-muted-foreground">Loading…</p>;
  }
  if (state.phase === "notfound") {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <Package className="text-muted-foreground" size={32} />
        <p className="text-muted-foreground">This bundle isn’t available.</p>
        <Link
          to="/shop/inventory"
          className="text-sm font-medium text-primary hover:underline"
        >
          Browse cards
        </Link>
      </div>
    );
  }
  if (state.phase === "error") {
    return (
      <p className="py-12 text-center text-destructive">
        Couldn’t load this bundle.
      </p>
    );
  }

  const { bundle, cards } = state.detail;
  const coverImg = bundle.coverRef
    ? imageUrl(bundle.coverRef, "original")
    : null;
  const inCart = cart.hasProduct(bundle.id);

  return (
    <div className="space-y-8">
      <Link
        to="/shop/inventory"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={14} />
        Back to cards
      </Link>

      {/* Featured hero — the cover photo is the star, details alongside it. */}
      <div className="grid gap-6 md:grid-cols-[minmax(0,340px)_1fr]">
        <div
          className="relative flex items-center justify-center overflow-hidden rounded-xl border border-primary/30 bg-card p-3"
          style={{ boxShadow: "0 0 28px rgba(201,168,76,0.15)" }}
        >
          <div
            className="absolute left-0 right-0 top-0 h-0.5"
            style={{
              background:
                "linear-gradient(90deg, transparent, var(--color-primary), transparent)",
            }}
          />
          {coverImg ? (
            <img
              src={coverImg}
              alt={bundle.name}
              className="max-h-[460px] w-full rounded-lg object-contain"
            />
          ) : (
            <div className="flex h-72 w-full items-center justify-center text-muted-foreground">
              <Package size={48} />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="flex items-center gap-1 rounded-sm px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider"
              style={{
                background: "rgba(201,168,76,0.18)",
                border: "1px solid rgba(201,168,76,0.4)",
                color: "var(--color-primary)",
              }}
            >
              <Package size={12} />
              Bundle
            </span>
            <span className="text-sm text-muted-foreground">
              {bundle.cardCount} card{bundle.cardCount === 1 ? "" : "s"}
              {bundle.productType ? ` · ${bundle.productType}` : ""}
            </span>
          </div>

          <h1 className="shop-display text-3xl font-bold leading-tight text-foreground">
            {bundle.name}
          </h1>

          <div
            className="shop-mono text-4xl font-bold"
            style={{ color: "var(--color-primary)" }}
          >
            ${bundle.price.toFixed(2)}
          </div>

          <p className="text-sm text-muted-foreground">
            A locked bundle — all {bundle.cardCount} card
            {bundle.cardCount === 1 ? "" : "s"} below ship together as one lot.
          </p>

          <div className="mt-1 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() =>
                inCart
                  ? cart.removeProduct(bundle.id)
                  : cart.addProduct(bundle.id)
              }
              className="inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold transition-colors"
              style={{
                background: inCart
                  ? "rgba(74,222,128,0.15)"
                  : "var(--color-primary)",
                border: inCart ? "1px solid rgba(74,222,128,0.4)" : "none",
                color: inCart ? "#4ade80" : "var(--color-primary-foreground)",
              }}
            >
              <ShoppingCart className="h-4 w-4" />
              {inCart ? "In cart — remove" : "Add to cart"}
            </button>
            {inCart && (
              <button
                type="button"
                onClick={() => cart.openDrawer()}
                className="text-sm font-semibold text-primary hover:underline"
              >
                View cart →
              </button>
            )}
          </div>
        </div>
      </div>

      {/* About — the long listing copy, de-emphasized below the hero. */}
      {bundle.description && (
        <section className="rounded-lg border border-border bg-card p-5">
          <h2 className="shop-display mb-2 text-lg font-semibold text-foreground">
            About this bundle
          </h2>
          <p
            className={`whitespace-pre-line text-sm leading-relaxed text-muted-foreground ${
              descExpanded ? "" : "line-clamp-[8]"
            }`}
          >
            {bundle.description}
          </p>
          <button
            type="button"
            onClick={() => setDescExpanded((v) => !v)}
            className="mt-2 text-xs font-semibold text-primary hover:underline"
          >
            {descExpanded ? "Show less" : "Show more"}
          </button>
        </section>
      )}

      {/* The bundle's cards (read-only) — compact thumbnail gallery; click a
          card for a quick-view closer look. */}
      <section>
        <h2 className="shop-display mb-3 text-lg font-semibold text-foreground">
          In this bundle ({cards.length})
        </h2>
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8">
          {cards.map((c, i) => {
            const img = c.imageFrontRef
              ? imageUrl(c.imageFrontRef, "thumb")
              : null;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setQuickIndex(i)}
                title={`${c.name} — quick view`}
                className="group overflow-hidden rounded-md border border-border bg-muted transition hover:border-primary/50"
              >
                <div
                  className="relative w-full overflow-hidden"
                  style={{ aspectRatio: "2.5 / 3.5" }}
                >
                  {img ? (
                    <img
                      src={img}
                      alt={c.name}
                      className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                      <Package size={20} />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <BundleQuickView
        cards={cards}
        index={quickIndex}
        onClose={() => setQuickIndex(null)}
        onNavigate={setQuickIndex}
      />
    </div>
  );
}

/**
 * Quick-view lightbox for a bundle's member cards — a closer look at one card
 * (large image + name / set / rarity / condition), with prev/next + Esc/arrow
 * keys. Read-only: members are part of the locked bundle, not bought singly.
 */
function BundleQuickView({
  cards,
  index,
  onClose,
  onNavigate,
}: {
  cards: StorefrontCard[];
  index: number | null;
  onClose: () => void;
  onNavigate: (i: number) => void;
}) {
  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && index > 0) onNavigate(index - 1);
      if (e.key === "ArrowRight" && index < cards.length - 1)
        onNavigate(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, cards.length, onClose, onNavigate]);

  if (index === null) return null;
  const c = cards[index];
  if (!c) return null;
  const img = c.imageFrontRef ? imageUrl(c.imageFrontRef, "original") : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md overflow-hidden rounded-xl border border-border bg-card"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-2 top-2 z-10 rounded-full bg-black/40 p-1.5 text-white hover:bg-black/60"
        >
          <X size={16} />
        </button>

        <div className="p-5">
          <div
            className="mx-auto w-full max-w-[280px] overflow-hidden rounded-lg bg-muted"
            style={{ aspectRatio: "2.5 / 3.5" }}
          >
            {img ? (
              <img
                src={img}
                alt={c.name}
                className="h-full w-full object-contain"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                <Package size={40} />
              </div>
            )}
          </div>
          <div className="mt-3 text-center">
            <h3 className="shop-display text-lg font-semibold text-foreground">
              {c.name}
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {c.setName}
              {c.setCode ? ` · ${c.setCode}` : ""}
              {c.cardNumber ? ` · ${c.cardNumber}` : ""}
            </p>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5">
              {c.rarity && (
                <span className="rounded-sm border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  {c.rarity}
                </span>
              )}
              {c.condition && (
                <span className="rounded-sm border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  {c.condition}
                </span>
              )}
            </div>
          </div>
        </div>

        {index > 0 && (
          <button
            type="button"
            onClick={() => onNavigate(index - 1)}
            aria-label="Previous card"
            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-1.5 text-white hover:bg-black/60"
          >
            <ChevronLeft size={18} />
          </button>
        )}
        {index < cards.length - 1 && (
          <button
            type="button"
            onClick={() => onNavigate(index + 1)}
            aria-label="Next card"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-1.5 text-white hover:bg-black/60"
          >
            <ChevronRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
}
